import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchCategories, fetchTags, fetchSettings } from "@/lib/api";
import { useState, useMemo } from "react";

export const Route = createFileRoute("/kategori/")({
  loader: async ({ context: { queryClient } }) => {
    const [categories, tags, settings] = await Promise.all([
      queryClient.ensureQueryData({ queryKey: ["categories"], queryFn: fetchCategories }),
      queryClient.ensureQueryData({ queryKey: ["tags"], queryFn: fetchTags }),
      queryClient.ensureQueryData({ queryKey: ["settings"], queryFn: fetchSettings }),
    ]);
    return { categories, tags, settings };
  },
  head: ({ loaderData }) => {
    const { settings } = loaderData as any;
    const siteName = settings?.site_name || "Lihar Aksara";
    const canonicalUrl = "https://liharaksara.web.id/kategori";
    return {
      meta: [
        { title: `Kategori & Topik — ${siteName}` },
        {
          name: "description",
          content: `Jelajahi tulisan berdasarkan topik pilihan di ${siteName}. Pilih topik yang sesuai dengan suasana hati Anda.`,
        },
        { property: "og:title", content: `Kategori & Topik — ${siteName}` },
        { property: "og:url", content: canonicalUrl },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: Page,
});

function Page() {
  const { data: categories } = useSuspenseQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  }) as any;

  const { data: tags } = useSuspenseQuery({
    queryKey: ["tags"],
    queryFn: fetchTags,
  }) as any;

  const [activeTab, setActiveTab] = useState<'categories' | 'genres' | 'tags'>('categories');

  const filteredItems = useMemo(() => {
    if (activeTab === 'tags') {
      return tags?.filter((t: any) => (t._count?.posts || 0) > 0).sort((a: any, b: any) => a.name.localeCompare(b.name)) || [];
    }
    return categories?.filter((c: any) => {
      const hasPosts = (c._count?.posts || 0) > 0;
      const isCorrectTab = activeTab === 'genres' ? c.isGenre : !c.isGenre;
      return hasPosts && isCorrectTab;
    }).sort((a: any, b: any) => a.name.localeCompare(b.name)) || [];
  }, [categories, tags, activeTab]);

  return (
    <div className="container-editorial py-16 md:py-24">
      <div className="mb-16">
        <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tight">Eksplorasi</h1>
        <p className="mt-6 text-xl text-muted-foreground font-serif max-w-2xl">
          Jelajahi ruang literasi ini melalui kategori, genre, atau tagar.
        </p>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-12 scrollbar-none border-b border-border/50">
        {[
          { id: 'categories', label: 'Kategori Artikel' },
          { id: 'genres', label: 'Genre Novel' },
          { id: 'tags', label: 'Tag Sastra' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-6 py-3 rounded-t-sm text-[10px] font-bold uppercase tracking-[0.2em] transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-accent/10 text-accent border-b-2 border-accent"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item: any) => (
          <Link
            key={item.slug}
            to={activeTab === 'tags' ? "/tag/$slug" : "/kategori/$slug"}
            params={{ slug: item.slug }}
            className="group block p-10 bg-card border border-border rounded-sm hover:shadow-xl hover:border-accent transition-all"
          >
            <h2 className="font-serif text-3xl font-medium group-hover:text-accent transition-colors flex items-center gap-3">
              {activeTab === 'tags' && <span className="text-muted-foreground/30">#</span>}
              {item.name}
            </h2>
            {item.description && (
               <p className="mt-3 text-muted-foreground line-clamp-2">{item.description}</p>
            )}
            <div className="mt-6 text-xs font-bold uppercase tracking-widest text-muted-foreground group-hover:text-accent transition-colors">
              {item._count?.posts || 0} Tulisan
            </div>
          </Link>
        ))}
      </div>

      {!filteredItems?.length && (
        <div className="py-24 text-center">
          <p className="text-muted-foreground font-serif italic text-lg opacity-60">
            Belum ada klasifikasi di bagian ini.
          </p>
        </div>
      )}
    </div>
  );
}
